import type { EnumUnion } from '@dresscode-frontend/utility-types';
import { type VariantProps, cva } from 'class-variance-authority';

import Icon, { type ColorVariantType } from '@/components/Icon/Icon';
import { Text } from '@/components/Typography/Text';
import { cn } from '@/lib/cn';

import { HStack, VStack } from '../Layout';

// AlertCardのバリアント定義
export const AlertCardVariant = {
  neutral: 'neutral',
  error: 'error',
  warning: 'warning',
  mutedError: 'mutedError',
  mutedWarning: 'mutedWarning',
  mutedPrimary: 'mutedPrimary',
  outline: 'outline-solid',
} as const;
export type AlertCardVariant = EnumUnion<typeof AlertCardVariant>;

// レイアウトオプション定義
export const AlertCardLayout = {
  default: 'default',
  vertical: 'vertical',
} as const;
export type AlertCardLayout = EnumUnion<typeof AlertCardLayout>;

const alertCardStyles = cva('flex rounded-lg border', {
  variants: {
    variant: {
      [AlertCardVariant.neutral]:
        'bg-neutral-fill border-neutral-border text-neutral-on-fill',
      [AlertCardVariant.error]:
        'bg-neutral-fill border-error-border text-error-on-neutral-fill',
      [AlertCardVariant.warning]:
        'bg-neutral-fill border-warning-border text-warning-on-neutral-fill',
      [AlertCardVariant.mutedError]:
        'bg-error-muted-fill border-error-border text-error-on-muted-fill',
      [AlertCardVariant.mutedWarning]:
        'bg-warning-muted-fill border-warning-border text-warning-on-muted-fill',
      [AlertCardVariant.mutedPrimary]:
        'bg-primary-muted-fill border-primary-muted-border text-primary-on-muted-fill',
      [AlertCardVariant.outline]:
        'bg-transparent border-neutral-border text-neutral-on-fill',
    },
    layout: {
      [AlertCardLayout.default]: 'flex-row gap-3 p-4',
      [AlertCardLayout.vertical]: 'flex-col gap-2 p-4',
    },
  },
});

const VARIANT_TO_ICON_COLOR_MAP: Record<AlertCardVariant, ColorVariantType> = {
  [AlertCardVariant.neutral]: 'neutral',
  [AlertCardVariant.outline]: 'neutral',
  [AlertCardVariant.error]: 'error',
  [AlertCardVariant.warning]: 'warningOnNeutral',
  [AlertCardVariant.mutedError]: 'errorOnMutedFill',
  [AlertCardVariant.mutedWarning]: 'warning',
  [AlertCardVariant.mutedPrimary]: 'neutral',
};

const getIconColorForVariant = (
  variant: AlertCardVariant,
): ColorVariantType => {
  return VARIANT_TO_ICON_COLOR_MAP[variant] ?? 'neutral';
};

export interface AlertCardProps extends VariantProps<typeof alertCardStyles> {
  /**
   * アラートのタイトル
   */
  title?: string;
  /**
   * アラートの説明文（単一）
   */
  description?: string;
  /**
   * アラートの説明文（複数・箇条書き）
   * descriptionsが指定された場合はdescriptionより優先されます
   */
  descriptions?: string[];
  /**
   * アラートのバリアント
   */
  variant?: AlertCardVariant;
  /**
   * レイアウトの種類
   */
  layout?: AlertCardLayout;
  /**
   * 追加のクラス名
   */
  className?: string;
  /**
   * HTMLのid属性
   */
  id?: string;
}

const determineContent = (
  props: Pick<AlertCardProps, 'title' | 'description' | 'descriptions'>,
) => {
  const hasTitle = Boolean(props.title);
  const hasDescriptions = Boolean(props.descriptions?.length);
  const hasDescription = Boolean(props.description) && !hasDescriptions;
  const hasAnyContent = hasTitle || hasDescription || hasDescriptions;

  return { hasTitle, hasDescription, hasDescriptions, hasAnyContent };
};

// Iconを描画
const renderAlertIcon = (variant: AlertCardVariant) => (
  <Icon
    iconName="AlertCircle"
    className="shrink-0"
    colorVariant={getIconColorForVariant(variant)}
    aria-hidden="true"
  />
);

// 箇条書きリストを描画
const renderDescriptionList = (descriptions: string[]) => (
  <ul className="space-y-1">
    {descriptions.map((desc, index) => (
      <li key={index} className="flex items-start">
        <span className="mr-2 mt-0.5 text-current">・</span>
        <Text variant="pSmall" className="flex-1">
          {desc}
        </Text>
      </li>
    ))}
  </ul>
);

// コンテンツ領域を描画
const renderContentArea = (
  props: Pick<AlertCardProps, 'title' | 'description' | 'descriptions'>,
) => {
  const { hasTitle, hasDescription, hasDescriptions } = determineContent(props);

  return (
    <VStack className="flex-1 min-w-0 gap-1.5">
      {hasTitle && <Text variant="label">{props.title}</Text>}
      {hasDescription && <Text variant="pSmall">{props.description}</Text>}
      {hasDescriptions &&
        props.descriptions &&
        renderDescriptionList(props.descriptions)}
    </VStack>
  );
};

/**
 * 重要な情報や状態を伝えるためのアラートカードコンポーネント
 *
 * 主な用途:
 * - エラーメッセージの表示
 * - 警告メッセージの表示
 * - 情報メッセージの表示
 * - 成功メッセージの表示
 *
 * 特徴:
 * - 複数のカラーバリアント（neutral、error、warning等）
 * - レイアウト選択（default、vertical）
 * - アイコンとテキストの適切な配置
 * - 単一説明文と複数説明文（箇条書き）の両対応
 * - アクセシビリティ対応
 */
export function AlertCard(props: AlertCardProps) {
  const {
    title,
    description,
    descriptions,
    variant = AlertCardVariant.neutral,
    layout = AlertCardLayout.default,
    className,
    id,
  } = props;

  const { hasAnyContent } = determineContent(props);

  // コンテンツが何もない場合は何も表示しない
  if (!hasAnyContent) {
    return null;
  }

  const containerClassName = cn(
    alertCardStyles({ variant, layout }),
    layout === AlertCardLayout.default && 'items-start',
    className,
  );

  return (
    <HStack
      id={id}
      className={containerClassName}
      role="alert"
      aria-live="polite"
    >
      {renderAlertIcon(variant)}
      {renderContentArea({ title, description, descriptions })}
    </HStack>
  );
}
